import { useEffect, useRef, useState } from 'react';
import { ApiDeliveryAddress, ApiList, ApiSchedule } from '../../types';
import { WIZARD_STEPS, WizardControl, WizardStep, missingSteps, stepNumber } from './wizard';

type SubscriptionWizard = {
	/* the step being asked for, or nothing when the wizard is not running */
	step?: WizardStep;
	/*
	 * What the modal standing in for a step needs, and only for the step it is standing in for.
	 * `ready` is for a step whose own saving is not the thing that finishes it - the products step,
	 * where the customer says when they are done and should not be able to say so with nothing added.
	 */
	wizardFor: (which: WizardStep, ready?: boolean) => WizardControl | undefined;
	/* ask again for whatever is still missing, which is what "continue setting up" does */
	resume: () => void;
	/* open one step on its own, which is what a row on the draft card asks for */
	start: (step: WizardStep) => void;
	/* an address chosen before there was a schedule to hang it on */
	pendingAddress?: ApiDeliveryAddress;
	setPendingAddress: (address: ApiDeliveryAddress) => void;
};

/*
 * Walks a half-made subscription through what it is still missing, one modal at a time. The steps
 * are worked out once, when the wizard starts: a step is taken off the front as it is saved, and
 * whatever is left when the customer closes a modal stays missing, which the card then says.
 */
export const useSubscriptionWizard = ({
	list,
	schedule,
	scheduleLoading,
	startWizard,
	onWizardEnd,
	onAbandoned,
}: {
	list: ApiList;
	schedule?: ApiSchedule;
	scheduleLoading: boolean;
	startWizard?: boolean;
	onWizardEnd?: () => void;
	/* only when a modal was closed on the way through, for what the steps had not saved yet */
	onAbandoned?: () => void;
}): SubscriptionWizard => {
	const [steps, setSteps] = useState<WizardStep[]>([]);
	const [pendingAddress, setPendingAddress] = useState<ApiDeliveryAddress | undefined>(undefined);
	const started = useRef(false);
	/*
	 * Which walk through the steps we are on. A save that is still in flight when the customer
	 * closes the modal lands afterwards and calls the `onDone` it was handed, which closed over a
	 * queue that is no longer the queue - and reopened the next modal over a wizard they had shut.
	 */
	const walk = useRef(0);
	/*
	 * A step opened on its own from the draft card is not a walk through four of them, so it is not
	 * numbered: the card is the list of steps, and it numbers them itself, five of them.
	 */
	const single = useRef(false);
	const step = steps[0];

	useEffect(() => {
		// the schedule decides where the wizard starts, so there is nothing to work out until it is in
		if (!startWizard || started.current || scheduleLoading) return;

		started.current = true;
		single.current = false;

		const missing = missingSteps(list, schedule);

		// nothing to ask about, so the page should stop pointing at this one
		if (!missing.length) return onWizardEnd?.();

		setSteps(missing);
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [startWizard, scheduleLoading]);

	const nextStep = (startedIn: number) => {
		if (startedIn !== walk.current) return;

		const left = steps.slice(1);

		setSteps(left);

		if (left.length) return;

		// nothing left to carry, and the page is still pointing at this subscription
		setPendingAddress(undefined);
		onWizardEnd?.();
	};

	const stop = () => {
		walk.current += 1;
		setSteps([]);
		setPendingAddress(undefined);
		onAbandoned?.();
		onWizardEnd?.();
	};

	return {
		step,
		wizardFor: (which, ready = true) => {
			if (step !== which) return undefined;

			const startedIn = walk.current;

			return {
				step: single.current ? undefined : stepNumber[which],
				total: WIZARD_STEPS,
				ready,
				onDone: () => nextStep(startedIn),
				onAbandon: stop,
			};
		},
		resume: () => {
			// a fresh walk, so anything still in flight from the last one has no say in this one
			walk.current += 1;
			single.current = false;
			setSteps(missingSteps(list, schedule));
		},
		start: (step) => {
			walk.current += 1;
			single.current = true;
			// just the one: the card is the list of steps, and it is where the customer comes back to
			setSteps([step]);
		},
		pendingAddress,
		setPendingAddress,
	};
};
